import { useEffect, useMemo } from 'react';

import {
  AlarmClockOffIcon,
  Clock01Icon,
  Configuration01Icon,
  InformationCircleIcon,
  RightToLeftListNumberIcon,
  SentIcon,
  TimeScheduleIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useWatch } from 'react-hook-form';

import BrandingTabSplitLayout from 'components/BrandingTabSplitLayout/BrandingTabSplitLayout';
import { InputGroup, Select, Toggle } from 'components/FormComponents';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import StatusPill from 'components/Pills/StatusPills';

const REMINDER_COUNT_OPTIONS = [
  { value: 1, label: '1 Reminder' },
  { value: 2, label: '2 Reminders' },
  { value: 3, label: '3 Reminders' },
];

const REMINDER_INTERVAL_OPTIONS = [
  { value: 24, labelSuffix: '24 hours' },
  { value: 48, labelSuffix: '48 hours' },
  { value: 72, labelSuffix: '72 hours' },
  { value: 168, labelSuffix: '1 week' },
];

function buildIntervalLabel(step, { labelSuffix }) {
  const after = step === 1 ? 'launch' : `${ordinal(step - 1)} reminder`;
  return `${labelSuffix} after ${after}`;
}

function ordinal(n) {
  const suffixes = ['', '1st', '2nd', '3rd'];
  return suffixes[n] ?? `${n}th`;
}

const RAIL_VARIANTS = {
  launch: { icon: SentIcon, circle: 'border-green-2/40 bg-green-1', iconCls: 'text-green-3' },
  terminal: { icon: Clock01Icon, circle: 'border-red-2/40 bg-red-1', iconCls: 'text-red-2' },
  mid: { icon: Clock01Icon, circle: 'border-blue-2/40 bg-blue-1', iconCls: 'text-blue-3' },
};

function ReminderTimelineRail({ variant = 'mid', isLast = false }) {
  const { icon: Icon, circle, iconCls } = RAIL_VARIANTS[variant] ?? RAIL_VARIANTS.mid;

  return (
    <div className="flex w-14 shrink-0 flex-col items-center pt-1">
      <div
        className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-full border',
          circle,
        )}>
        <HugeiconsIcon icon={Icon} size={20} strokeWidth={1.5} className={iconCls} />
      </div>
      {!isLast && (
        <div
          className="my-2 min-h-10 w-0 flex-1 border-l-2 border-dashed border-neutral-4"
          aria-hidden
        />
      )}
    </div>
  );
}

function OverdueAtReminderToggle({ step, overdueValue, setValue, readOnly = false }) {
  const current = Number(overdueValue);
  const checked = current === step;

  function handleChange(e) {
    if (!setValue) return;

    if (e.target.checked) {
      setValue('overdue_after_reminder', step, { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (checked) {
      const fallback = step > 1 ? step - 1 : 1;
      setValue('overdue_after_reminder', fallback, { shouldDirty: true, shouldValidate: true });
    }
  }

  return (
    <div className="flex items-center gap-3">
      <Toggle
        register={() => ({})}
        disabled={readOnly}
        small
        inputProps={{ checked, onChange: handleChange }}
      />
      <span className="text-xl text-text-tertiary">Mark as overdue at this stage</span>
    </div>
  );
}

function ReminderStep({
  step,
  fieldName,
  fieldValue,
  register,
  overdueAfterReminder,
  setValue,
  isLast = false,
  readOnly = false,
}) {
  const isOverdueTrigger = Number(overdueAfterReminder) === step;
  const isOverdueFromHereOn = Number(overdueAfterReminder) <= step && isOverdueTrigger;
  const railVariant = isOverdueTrigger ? 'terminal' : 'mid';
  const borderCls = isOverdueTrigger ? 'border-red-2/40' : 'border-blue-2/40';

  return (
    <div className="flex gap-5">
      <ReminderTimelineRail variant={railVariant} isLast={isLast} />
      <div className="min-w-0 flex-1 pb-6">
        <div className={cn('flex flex-col gap-4 rounded-3xl border p-6', borderCls)}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-2xl font-medium text-neutral-7">Reminder {step}</span>
            {isOverdueTrigger && <StatusPill status="error" text="Overdue trigger" small />}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2">
            <InputGroup noMargin>
              <Select name={fieldName} register={register} value={fieldValue} useDefault>
                {REMINDER_INTERVAL_OPTIONS.map(opt => (
                  <Select.Item key={opt.value} value={opt.value}>
                    {buildIntervalLabel(step, opt)}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
          </div>

          <div className="flex flex-col gap-2">
            <OverdueAtReminderToggle
              step={step}
              overdueValue={overdueAfterReminder}
              setValue={setValue}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function RemindersEmptyState() {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-8">
      <HugeiconsIcon
        icon={AlarmClockOffIcon}
        size={80}
        strokeWidth={1.5}
        className="text-neutral-6"
      />
      <div className="flex w-full items-center justify-center gap-2">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={20}
          strokeWidth={1.5}
          className="text-neutral-6"
        />
        <p className="text-xl text-text-tertiary">
          Reminders are turned off. Enable the toggle above to set follow ups and overdue statuses
          for your candidates and referees.
        </p>
      </div>
    </div>
  );
}

const REMINDER_STEPS = [
  { step: 1, fieldName: 'first_reminder' },
  { step: 2, fieldName: 'second_reminder' },
  { step: 3, fieldName: 'third_reminder' },
];

export const Reminders = ({
  brand,
  control,
  register,
  setValue,
  readOnly = false,
  backgroundColor = 'background-secondary',
}) => {
  const remindersActive = useWatch({ control, name: 'reminders_active' });
  const remindersValue = useWatch({ control, name: 'reminders' });
  const firstReminderValue = useWatch({ control, name: 'first_reminder' });
  const secondReminderValue = useWatch({ control, name: 'second_reminder' });
  const thirdReminderValue = useWatch({ control, name: 'third_reminder' });
  const overdueAfterReminder = useWatch({ control, name: 'overdue_after_reminder' });

  const fieldValues = {
    first_reminder: firstReminderValue,
    second_reminder: secondReminderValue,
    third_reminder: thirdReminderValue,
  };

  const reminderCount = useMemo(() => {
    const n = Number(remindersValue);
    return n >= 1 && n <= 3 ? n : 1;
  }, [remindersValue]);

  const overdueTriggerStep = useMemo(() => {
    const o = Number(overdueAfterReminder);
    const safe = Number.isFinite(o) && o >= 1 ? o : 1;
    return Math.min(safe, reminderCount);
  }, [overdueAfterReminder, reminderCount]);

  useEffect(() => {
    if (!setValue) return;
    const o = Number(overdueAfterReminder);
    if (Number.isFinite(o) && o > reminderCount) {
      setValue('overdue_after_reminder', reminderCount, { shouldDirty: true });
    }
  }, [reminderCount, overdueAfterReminder, setValue]);

  const activeSteps = REMINDER_STEPS.slice(0, reminderCount);

  return (
    <BrandingTabSplitLayout key={brand?.id} shellClassName={backgroundColor}>
      <FormMessage
        message="Reminder notifications are sent via email. SMS is not currently supported for reminders."
        type={MessageType.Info}
        closeable={false}
        noMargin
      />

      <div className="flex flex-col gap-8">
        <section className="flex flex-col gap-2 rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex w-3/4 flex-grow-1 gap-2">
              <HugeiconsIcon
                icon={TimeScheduleIcon}
                size={24}
                className="shrink-0 text-neutral-6"
              />
              <div className="mb-4 flex flex-col gap-2">
                <span className="text-2xl font-medium text-neutral-7">Reminders</span>
                <p className="text-xl text-text-tertiary">
                  Set follow ups and overdue statuses for your candidates and referees.
                </p>
              </div>
            </div>
            <div className="w-auto flex-shrink-0">
              <InputGroup noMargin>
                <Toggle register={register} name="reminders_active" small />
              </InputGroup>
            </div>
          </div>

          <div className="flex w-full flex-col items-center justify-center gap-4 border-t border-neutral-4 pt-8">
            {remindersActive ? (
              <section className="flex w-full flex-col gap-8">
                <article className="flex flex-col gap-8">
                  <div className="flex gap-2">
                    <HugeiconsIcon
                      icon={RightToLeftListNumberIcon}
                      size={24}
                      className="shrink-0 text-neutral-6"
                    />
                    <div className="flex flex-col gap-2">
                      <span className="text-2xl font-medium text-neutral-7">
                        Number of Reminders
                      </span>
                      <p className="text-xl text-text-tertiary">
                        Set the number of reminders you want to send to your candidates and
                        referees.
                      </p>
                    </div>
                  </div>
                  <InputGroup noMargin>
                    <Select name="reminders" register={register} value={remindersValue} useDefault>
                      {REMINDER_COUNT_OPTIONS.map(({ value, label }) => (
                        <Select.Item key={value} value={value}>
                          {label}
                        </Select.Item>
                      ))}
                    </Select>
                  </InputGroup>
                </article>

                <article className="flex flex-col gap-8">
                  <div className="flex gap-2">
                    <HugeiconsIcon
                      icon={Configuration01Icon}
                      size={24}
                      className="shrink-0 text-neutral-6"
                    />
                    <div className="flex flex-col gap-2">
                      <span className="text-2xl font-medium text-neutral-7">
                        Reminder configuration
                      </span>
                      <p className="text-xl text-text-tertiary">
                        Set the time intervals for each reminder and the status to overdue on
                        selected reminder.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-0">
                    <div className="flex gap-5">
                      <ReminderTimelineRail variant="launch" isLast={false} />
                      <div className="min-w-0 flex-1 pb-6">
                        <div className="flex flex-col gap-4 rounded-3xl border border-green-2/40 p-6">
                          <div className="flex flex-col gap-2">
                            <span className="text-2xl font-medium text-neutral-7">Launch</span>
                            <span className="text-xl text-text-tertiary">
                              When the check is submitted, the first email is sent.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {activeSteps.map(({ step, fieldName }, idx) => (
                      <ReminderStep
                        key={step}
                        step={step}
                        fieldName={fieldName}
                        fieldValue={fieldValues[fieldName]}
                        register={register}
                        overdueAfterReminder={overdueTriggerStep}
                        setValue={setValue}
                        isLast={idx === activeSteps.length - 1}
                        readOnly={readOnly}
                      />
                    ))}
                  </div>
                </article>
              </section>
            ) : (
              <RemindersEmptyState />
            )}
          </div>
        </section>
      </div>
    </BrandingTabSplitLayout>
  );
};
